import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

const PANEL_WIDTH = 256;

export interface CancelReasonProps {
  /** The request number, for the button's accessible name. */
  reference?: string;
  /** Staff's reason, as written; undefined when staff gave none. */
  reason?: string;
}

/**
 * "Why?" beside a cancelled request (MYP-63): the old website's ? next to 取消. One tap shows staff's
 * reason as written — the only explanation the customer gets — or says that none was given.
 *
 * Placed against the window (`fixed`), like the Wallet's receipt menu, so the table's sideways
 * scroll cannot cut it off; below the button, or above when there is no room. It closes on Esc
 * (focus goes back to the button), a click outside, scroll or resize.
 */
export function CancelReason({ reference, reason }: CancelReasonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current?.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight ?? 0;
    if (!button) return;
    const below = button.bottom + 6;
    const top = below + height > window.innerHeight - 8 ? button.top - 6 - height : below;
    const left = Math.min(Math.max(8, button.left), window.innerWidth - PANEL_WIDTH - 8);
    setPlace({ top, left });
    return () => setPlace(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    };
    const onMove = () => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={
          reference
            ? t('chinaPayment.records.whyFor', { reference })
            : t('chinaPayment.records.why')
        }
        onClick={() => setOpen((previous) => !previous)}
        className={cn(
          'inline-flex min-h-11 items-center gap-1 rounded-md px-1.5 text-xs font-semibold text-danger',
          'hover:bg-danger/10 lg:min-h-7',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        )}
      >
        <Icon name="help" size={16} />
        {t('chinaPayment.records.why')}
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="status"
          style={{
            top: place?.top ?? 0,
            left: place?.left ?? 0,
            width: PANEL_WIDTH,
            visibility: place ? 'visible' : 'hidden',
          }}
          className="fixed z-50 grid gap-1 rounded-lg bg-surface p-3 text-sm shadow-raised ring-1 ring-black/5"
        >
          <p className="text-xs font-semibold text-muted">
            {t('chinaPayment.records.reasonTitle')}
          </p>
          <p className={cn('break-words whitespace-pre-wrap', reason ? 'text-ink' : 'text-muted')}>
            {reason ?? t('chinaPayment.records.noReason')}
          </p>
        </div>
      ) : null}
    </>
  );
}
